import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Radio, Layers, Video, Gamepad2, ArrowRight, ChevronRight, type LucideIcon } from 'lucide-react';

// The three creator paths, framed by what you make + who you are (per the
// design handoff, specs/mystore-creation-flow-*.md). Icons are lucide only —
// never Film/Clapperboard. Accents are darkened for legibility ON the beige
// card (glyph/persona), full-strength on hover (border).
export type ListingType = 'webinar' | 'course' | 'video' | 'series' | 'gameshow';

export interface CreateTypeDef {
  key: ListingType;
  title: string;
  description: string;
  persona: string;
  icon: LucideIcon;
  tint: string;   // icon-tile background
  glyph: string;  // darkened accent — icon + persona text on beige
  hover: string;  // full-strength accent — hover border
  typeHref: string; // direct route to this type's builder
}

export const CREATE_TYPES: CreateTypeDef[] = [
  {
    key: 'webinar',
    title: 'Featured event or class',
    description: 'Host a live session — a Q&A, workshop, or class. Buyers register and get the join link.',
    persona: 'Popular with agents',
    icon: Radio,
    tint: 'var(--hj-pink-tint)',
    glyph: '#b3438f',
    hover: '#f286c9',
    typeHref: '/my-store/new/webinar',
  },
  {
    key: 'course',
    title: 'On-demand course',
    description: 'Package videos and PDFs into modules and lessons people work through anytime.',
    persona: 'Popular with casting directors',
    icon: Layers,
    tint: 'var(--hj-yellow-tint)',
    glyph: '#9a7d05',
    hover: '#ffd600',
    typeHref: '/my-store/course/new',
  },
  {
    key: 'video',
    title: 'Video, show or series',
    description: 'Sell a single video or an episodic series straight from your Hey Joe projects.',
    persona: 'Popular with talent',
    icon: Video,
    tint: 'rgb(65 113 249 / 0.12)',
    glyph: '#3a5fd0',
    hover: '#4171f9',
    typeHref: '/my-store/new/video',
  },
  {
    key: 'series',
    title: 'Develop a series',
    description: 'Story, characters, episodes, beats — we build a vertical comedy series with you. You write the funny.',
    persona: 'Popular with creators',
    icon: Video,
    tint: 'rgb(65 113 249 / 0.12)',
    glyph: '#3a5fd0',
    hover: '#4171f9',
    typeHref: '/my-store/series-studio',
  },
];

// A game show IS a featured event product (ticketing/dates/pricing) plus a linked show
// engine — surfaced as its own create card. Gated to dev hosts by the caller.
export const GAME_SHOW_TYPE: CreateTypeDef = {
  key: 'gameshow',
  title: 'Game show',
  description: 'Host a live trivia night or game show. The audience plays along on their phones.',
  persona: 'New',
  icon: Gamepad2,
  tint: 'var(--hj-pink-tint)',
  glyph: '#b3438f',
  hover: '#f286c9',
  typeHref: '/my-store/new/webinar?format=gameshow',
};

function CreateCard({ def, to }: { def: CreateTypeDef; to: string }) {
  const [hover, setHover] = useState(false);
  const Icon = def.icon;
  const canHover = typeof window !== 'undefined' && window.matchMedia?.('(hover: hover)').matches;
  const lifted = hover && canHover;

  const iconTile = (size: number) => (
    <span
      className="inline-flex items-center justify-center flex-shrink-0"
      style={{ width: size, height: size, borderRadius: 11, background: def.tint, color: def.glyph }}
    >
      <Icon style={{ width: 20, height: 20 }} strokeWidth={2} />
    </span>
  );

  return (
    <Link
      to={to}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'block',
        background: 'var(--hj-beige)',
        border: `1px solid ${hover ? def.hover : 'var(--hj-beige-line)'}`,
        borderRadius: 'var(--hj-r-md)',
        textDecoration: 'none',
        color: 'var(--hj-beige-ink)',
        transform: lifted ? 'translateY(-3px)' : 'none',
        transition: 'transform 160ms ease, border-color 160ms ease',
      }}
    >
      {/* Mobile — full-width row (icon · text · chevron) */}
      <div className="flex sm:hidden items-center gap-3" style={{ padding: 16, minHeight: 68 }}>
        {iconTile(44)}
        <div className="flex-1 min-w-0">
          <div className="font-hj-display" style={{ fontSize: 15.5, fontWeight: 700, color: 'var(--hj-beige-ink)', letterSpacing: '-0.01em' }}>
            {def.title}
          </div>
          <div className="mono-label" style={{ marginTop: 3, color: def.glyph }}>{def.persona}</div>
        </div>
        <ChevronRight className="w-5 h-5 flex-shrink-0" style={{ color: 'var(--hj-beige-ink-3)' }} />
      </div>

      {/* Desktop — vertical card (icon · title · description · footer) */}
      <div className="hidden sm:flex flex-col h-full" style={{ padding: 18 }}>
        {iconTile(42)}
        <div className="font-hj-display" style={{ marginTop: 14, fontSize: 16, fontWeight: 700, color: 'var(--hj-beige-ink)', letterSpacing: '-0.01em' }}>
          {def.title}
        </div>
        <p className="m-0" style={{ marginTop: 6, fontSize: 12.5, lineHeight: 1.5, color: 'var(--hj-beige-ink-2)' }}>
          {def.description}
        </p>
        <div
          className="flex items-center justify-between"
          style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid var(--hj-beige-line)' }}
        >
          <span className="mono-label" style={{ color: def.glyph }}>{def.persona}</span>
          <ArrowRight className="w-4 h-4" style={{ color: def.glyph }} />
        </div>
      </div>
    </Link>
  );
}

// hrefMode 'hub' → cards route to the shared chooser (/my-store/new) so the AI
// "describe it" classifier can prefill the listing builder.
// hrefMode 'type' → each card routes straight to its builder.
// Exception: 'series' always goes straight to the Series Studio — it has its own
// "describe your series" pitch flow, so bouncing through the chooser first just
// makes the creator pick the type twice.
export function CreateTypeGrid({ hrefMode, includeGameShow }: { hrefMode: 'hub' | 'type'; includeGameShow?: boolean }) {
  const types = includeGameShow ? [...CREATE_TYPES, GAME_SHOW_TYPE] : CREATE_TYPES;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
      {types.map(def => (
        <CreateCard key={def.key} def={def} to={def.key === 'series' || def.key === 'gameshow' || hrefMode === 'type' ? def.typeHref : '/my-store/new'} />
      ))}
    </div>
  );
}
